import React from "react";
import LoanStatusBadge from './LoanStatusBadge';
import LoanTypeIcon from './LoanTypeIcon';
import InstallmentProgressBar from './InstallmentProgressBar';
import PaymentPlanStatusBadge from './PaymentPlanStatusBadge';
import { formatDate } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/formatCurrency';
import {
    DISPLAY_MODE,
    isOperationalPaymentPlanMode,
    resolveDisplayMode,
} from '../../utils/paymentPlanDisplay';

function Loan({ data, token, handleLoanClick, installments, client, className = 'mb-3' }) {
    const displayMode = resolveDisplayMode(data);
    const isActivePlan = isOperationalPaymentPlanMode(displayMode);
    const paymentPlan = data.payment_plan ?? null;
    const showPlanBadge = displayMode !== DISPLAY_MODE.REGULAR_LOAN;

    return (
        <div className={`card border-0 shadow-sm ${className}`}>
            <div className="card-body">
                {/* Encabezado del préstamo */}
                <div className="d-flex justify-content-between align-items-start mb-3" 
                     role="button" 
                     onClick={() => handleLoanClick(data.uid)}
                     style={{ cursor: 'pointer' }}>
                    <div className="d-flex align-items-center gap-2">
                        <LoanTypeIcon type={data.type} />
                        <h6 className="mb-0">{data.product}</h6>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-1">
                        {showPlanBadge ? (
                            <PaymentPlanStatusBadge
                                status={paymentPlan?.status ?? data.last_payment_plan?.status}
                                displayMode={displayMode}
                                statusLabel={paymentPlan?.status_label ?? data.last_payment_plan?.status_label}
                            />
                        ) : (
                            <LoanStatusBadge status={data.status.code} />
                        )}
                    </div>
                </div>

                {/* Información básica */}
                <div className="row g-3 mb-3 text-muted small">
                    <div className="col-4 text-center" title="Fecha">
                        <div className="d-flex align-items-center justify-content-center">
                            <i className="bi bi-calendar3 me-1"></i>
                            <strong className="mt-1">{formatDate(data.issued_date)}</strong>                            
                        </div>
                    </div>
                    <div className="col-4 text-center" title="Capital">
                        <div className="d-flex align-items-center justify-content-center">
                            <i className="bi bi-cash-stack me-1"></i>
                            <strong className="mt-1">{formatCurrency(data.financial.capital, { maximumFractionDigits: 0 })}</strong>
                        </div>
                    </div>
                    <div className="col-4 text-center" title={isActivePlan ? 'Plan de pagos' : 'Plan de cuotas'}>
                        {isActivePlan && paymentPlan ? (
                            <div className="d-flex flex-column align-items-center">
                                <div className="d-flex align-items-center justify-content-center">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    <strong className="mt-1 text-success">
                                        {formatCurrency(paymentPlan.remaining_amount, { maximumFractionDigits: 0 })}
                                    </strong>
                                </div>
                                {paymentPlan.next_due_date && (
                                    <small className="text-muted">
                                        Vence {formatDate(paymentPlan.next_due_date)}
                                    </small>
                                )}
                            </div>
                        ) : (
                            <div className="d-flex align-items-center justify-content-center">
                                <i className="bi bi-calendar-range me-1"></i>
                                <strong className="mt-1">{data.financial.installments_info.count} cuotas</strong>
                            </div>
                        )}
                    </div>
                </div>

                {!isActivePlan &&
                    data.financial?.installments_info?.count > 0 &&
                    client?.configuration?.show_progress_bar && (
                    <div className="mt-2 mb-2">
                        <InstallmentProgressBar
                            installments={installments ?? []}
                            totalCount={data.financial.installments_info.count}
                        />
                    </div>
                )}

                <div className="row">
                    <div className="col-12 mt-2">
                        <button 
                            className="btn btn-link btn-sm text-primary p-0"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLoanClick(data.uid);
                            }}
                        >
                            <i className="bi bi-chevron-right me-1"></i>
                            Ver detalle completo
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default Loan;
